import BugsnagPluginReact from '@bugsnag/plugin-react';
import { PricingContextProvider } from '@vp/react-pricing-context';
import TrackingJsModule from '@vp/react-tracking';
import React, { useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import { initialize } from '@vp/ab-reader';
import { ErrorBoundary } from '@vp/swan';
import { AppRoutes, SwanConfiguration } from '@99designs/briefs';
import { useSetupCtcLocally } from '@99designs/design-services-common';
import { IdentityProvider, buildAuthConfiguration } from '@99designs/design-services-common';
import { AlertProvider } from '@99designs/design-services-layouts';
import {
    BriefProvider,
    ProductOptionsProvider,
    StatsigConfiguration,
} from '@99designs/feature-design-services-brief';
import { AuthenticatedApolloProvider } from '@99designs/graph-utils';
import { getCountryCode, getCurrentCultureWithDev } from '@99designs/i18n';
import i18n from '../lib/i18n';

Bugsnag.start({
    apiKey: process.env.VITE_BRIEFS_BUGSNAG_API_KEY ?? '',
    enabledReleaseStages: ['production'],
    releaseStage: process.env.VITE_BRIEFS_BUGSNAG_RELEASE_STAGE,
    autoTrackSessions: process.env.VITE_BRIEFS_BUGSNAG_RELEASE_STAGE === 'production',
    plugins: [new BugsnagPluginReact(React)],
    onError: (event) => {
        event.addMetadata('request', {
            url: event.request.url,
            headers: event.request.headers,
        });

        event.addMetadata('debug info', {
            errorLocation: typeof window === 'undefined' ? 'server' : 'client',
        });
    },
});

/**
 * The below import structure is a workaround for the fact that these packages are not built as ES
 * Modules.
 *
 * At dev-time the import is correctly importing just the default export, but at build time the
 * import is importing the entire module.
 *
 * We therefore have to use the default key to get the default export.
 *
 * See: https://github.com/vitejs/vite/issues/4209
 */
const TrackingJs = (TrackingJsModule as any).default ?? TrackingJsModule; // eslint-disable-line @typescript-eslint/no-explicit-any

function getEnvironment() {
    if (
        window.location.hostname.includes('staging') ||
        window.location.hostname.includes('localhost')
    ) {
        return 'non-production';
    }
    return 'production';
}

function App() {
    const currentCulture = useMemo(getCurrentCultureWithDev, []);

    void i18n.changeLanguage(currentCulture);

    const authConfig = useMemo(() => {
        return buildAuthConfiguration(
            process.env.VITE_BRIEFS_HOSTNAME || '',
            {
                internalAccess: 'wU1R9YyaNcvmExIicqGonvUVQGDkJMOr',
                directAccess: '4e301fd6cd21daa1423a7db2697e42b0',
            },
            /^https?:\/\/(.+\.growth\.99cluster\.com)/
        );
    }, []);

    window.addEventListener('unhandledrejection', (event) => {
        console.error('unhandled rejection reason:', event.reason);
    });

    useSetupCtcLocally(process.env.VITE_BRIEFS_BUGSNAG_RELEASE_STAGE !== 'production');

    // call this as soon as possible in your outermost component
    initialize();

    // Note: We used to have a `BriefExperimentsProvider` that may help with experiments, but it was
    //       removed since it was not being used; see: `b170577e6890fdf3ff1ee632c03d7e4b633edcad`.

    return (
        <AlertProvider style={{ margin: '60px' }}>
            <ErrorBoundary>
                <SwanConfiguration locale={currentCulture}>
                    <IdentityProvider locale={currentCulture} {...authConfig}>
                        <BrowserRouter>
                            <AuthenticatedApolloProvider
                                locale={currentCulture}
                                environment={getEnvironment()}
                                endpoint={process.env.VITE_BRIEFS_GRAPHQL_ENDPOINT || ''}
                            >
                                <PricingContextProvider
                                    initializationConfig={{
                                        merchantId: 'vistaprint',
                                        market: getCountryCode(currentCulture),
                                        isDevelopmentMode: getEnvironment() === 'non-production',
                                    }}
                                >
                                    <StatsigConfiguration>
                                        <TrackingJs
                                            tenant="vistaprint"
                                            culture={currentCulture}
                                            environment={
                                                process.env.VITE_BRIEFS_TRACKING_ENV || 'dev'
                                            }
                                        />
                                        <BriefProvider>
                                            <ProductOptionsProvider>
                                                <AppRoutes currentCulture={currentCulture} />
                                            </ProductOptionsProvider>
                                        </BriefProvider>
                                    </StatsigConfiguration>
                                </PricingContextProvider>
                            </AuthenticatedApolloProvider>
                        </BrowserRouter>
                    </IdentityProvider>
                </SwanConfiguration>
            </ErrorBoundary>
        </AlertProvider>
    );
}

export default App;
